import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Slider from 'react-slick';
import { withRouter } from 'react-router';
import Icon from 'components/Icon';
import VisibilitySensor from 'react-visibility-sensor';
import CarouselV2 from 'componentsV2/Carousel';
import { Hero, COLLECTION } from 'componentsV2/Hero';
import {
  SITE_PLATFORM_MOBILE,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
} from 'helpers/BreakpointHelper';
import { userAttributeEvents as optimizelyUserAttributeEvents } from 'helpers/OptimizelyHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  additionalPosterEnabled,
  carouselV2Enabled,
  immersiveShowcaseEnabled,
} from 'helpers/ConfigHelper';
import { Element } from 'react-scroll';
import { Waypoint } from 'react-waypoint';
import Style from '../../Collection.module.scss';

const ALL_COLLECTIONS_SCROLL_PREFIX = 'ALL_COLLECTIONS_SCROLL_PREFIX_';
const BREAKPOINTS = (() => {
  const breakpoints = {
    [SITE_PLATFORM_WEB_LARGE]: {
      allowInfoToggle: false,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: true,
    },
    [SITE_PLATFORM_WEB]: {
      allowInfoToggle: false,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: true,
    },
    [SITE_PLATFORM_TABLET]: {
      allowInfoToggle: true,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: false,
    },
    [SITE_PLATFORM_MOBILE]: {
      allowInfoToggle: true,
      showLiveButtons: true,
      smallCollectionHeader: false,
      useSeriesLogo: true,
      arrows: false,
    },
  };

  return breakpoints;
})();

function SampleNextArrow(props) {
  const { className = null, onClick = () => {}, customClassName = null } = props;
  return (
    <button
      className={classNames(className, customClassName)}
      onClick={onClick}
      aria-label="show next content"
    >
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
    </button>
  );
}

SampleNextArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  customClassName: PropTypes.string,
};

function SamplePrevArrow(props) {
  const { className = null, onClick = () => {}, customClassName = null } = props;
  return (
    <button
      className={classNames(className, customClassName)}
      onClick={onClick}
      aria-label="show previous content"
    >
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
    </button>
  );
}

SamplePrevArrow.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  customClassName: PropTypes.string,
};

class HeroCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = { currentSlide: 0 };
  }

  render() {
    const {
      accessToken,
      breakpoint,
      bookmarks,
      callCollectionImpressionEvent,
      handleSlideChangeTop,
      idx,
      panel,
      play,
      profileId,
    } = this.props;
    const { currentSlide } = this.state;
    const { arrows } = BREAKPOINTS[breakpoint];
    const { showcaseCarouselAutoscrollSeconds } = window.ExternalConfig;
    const { id: panelId, items, name: collectionName } = panel;
    if (!items) return null;
    const slideClassName = classNames(
      Style.slide,
      Style.first,
      Style.heroPanel,
      additionalPosterEnabled() ? Style.additionalPoster : null,
      currentSlide === idx ? Style.active : null,
      currentSlide === idx && !play ? Style.inactive : null,
    );
    const headerContainerClass = classNames(
      Style.homeSlideHeaderContainer,
      Style.firstHeaderContainer,
      immersiveShowcaseEnabled() && Style.immersiveCarousel,
    );
    const isCarousel = items.member.length > 1;
    const heroItems = items.member.map((item, idx) => (
      <Hero
        accessToken={accessToken}
        bookmarks={bookmarks}
        profileId={profileId}
        callCollectionImpressionEvent={callCollectionImpressionEvent}
        idx={idx}
        isOffset={isCarousel}
        item={item}
        panel={panel}
        type={COLLECTION}
        key={item?.id}
      />
    ));

    const autoplaySpeed = showcaseCarouselAutoscrollSeconds * 1000 || 8000;
    const renderCarousel = carouselV2Enabled() ? (
      <CarouselV2 type="showcase">{heroItems}</CarouselV2>
    ) : (
      <VisibilitySensor
        onChange={(isVisible, next) => {
          if (isVisible) this.slider.slickPlay();
          else this.slider.slickPause();
        }}
      >
        <Slider
          dots
          arrows={arrows}
          infinite
          autoplay
          useTransform={false}
          slidesToShow={1}
          autoplaySpeed={autoplaySpeed}
          ref={slider => {
            this.slider = slider;
          }}
          appendDots={dots => (
            <div className={Style.dotsContainer}>
              <ul className={Style.dotsList}>{dots}</ul>
            </div>
          )}
          customPaging={() => <div className={Style.dots} />}
          nextArrow={<SampleNextArrow customClassName={Style.rightArrow} />}
          prevArrow={<SamplePrevArrow customClassName={Style.leftArrow} />}
          beforeChange={(current, next) => {
            this.setState({ currentSlide: next });
          }}
          trayProps={{ role: 'listbox' }}
        >
          {heroItems}
        </Slider>
      </VisibilitySensor>
    );
    const renderSinglItem = <div className={Style.singleSlide}>{heroItems}</div>;

    return (
      <div key={`videoPanel-${panelId}-${idx}`} id={panelId} className={slideClassName}>
        <Element className={headerContainerClass} name={`${ALL_COLLECTIONS_SCROLL_PREFIX}${idx}`}>
          <Waypoint
            onEnter={() => {
              handleSlideChangeTop(idx, true);
              optimizelyUserAttributeEvents().collectionItem(collectionName);
            }}
            onLeave={() => handleSlideChangeTop(idx, false)}
            topOffset="250px"
          >
            <div>{isCarousel ? renderCarousel : renderSinglItem}</div>
          </Waypoint>
        </Element>
      </div>
    );
  }
}

HeroCarousel.defaultProps = {
  accessToken: null,
  account: null,
  anonymousAccount: null,
  blackoutConfig: {},
  blackoutDefinitions: {},
  coordinates: null,
  favoritesManager: {},
  handleSlideChangeTop: noop,
  idx: null,
  initialShowcase: null,
  location: {},
  paginatedPanels: {},
  panelVideo: {},
  panel: {},
  panels: {},
  params: { network: null },
  play: null,
  provider: {},
  route: {},
  seo: null,
  visibleCollections: {},
};

HeroCarousel.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({
    accessToken: PropTypes.string,
  }),
  anonymousAccount: PropTypes.shape({}),
  blackoutConfig: PropTypes.shape({}),
  blackoutDefinitions: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  bookmarks: PropTypes.shape({}).isRequired,
  coordinates: PropTypes.shape({}),
  callCollectionImpressionEvent: PropTypes.func,
  favoritesManager: PropTypes.shape({}),
  handleSlideChangeTop: PropTypes.func,
  idx: PropTypes.number,
  initialShowcase: PropTypes.shape({}),
  location: PropTypes.shape({}),
  paginatedPanels: PropTypes.shape({}),
  panel: PropTypes.shape({}),
  panelVideo: PropTypes.shape({}),
  params: PropTypes.shape({
    network: PropTypes.string,
  }),
  panels: PropTypes.shape({
    itemsPerPage: PropTypes.number,
    member: PropTypes.arrayOf(PropTypes.shape({})),
    totalItems: PropTypes.number,
  }),
  play: PropTypes.bool,
  profileId: PropTypes.string,
  provider: PropTypes.shape({}),
  route: PropTypes.shape({}),
  router: PropTypes.shape({}).isRequired,
  seo: PropTypes.shape({}),
  visibleCollections: PropTypes.shape({}),
};

export default withRouter(HeroCarousel);
